import type { Folder } from "~/types/folder"
import type { PageData } from "~/types/pages"
import { useQuery } from "@tanstack/vue-query"
import { ContentType } from "~/models/Content/ContentType"

type MarketingData = Array<Folder | PageData>

export function useMarketing<T>(select: (items: MarketingData) => T) {
  const { cms } = useRuntimeConfig().public

  const getContent = useGetContent()

  return useQuery({
    queryKey: ["marketing", cms.pagesLocationId] as const,
    staleTime: 60 * 60 * 1000, // 1 hour
    queryFn({ queryKey }) {
      return getContent({
        childDepth: 3,
        contentType: [ContentType.Page, ContentType.Folder],
        parentLocationId: [Number.parseInt(queryKey[1], 10)],
        sortField: "priority",
      })
    },
    select,
  })
}

export function selectPages(items: MarketingData) {
  return items
    .filter(isContentType(ContentType.Page))
    .map(page => ({
      ...page,
      sections: page.children.filter(isContentType(ContentType.PageSection)),
    }))
}

export function selectMarketingArticles(items: MarketingData) {
  return items
    .filter(isContentType(ContentType.Folder))
    .flatMap(it => it.children)
    .filter(isContentType(ContentType.MarketingArticle))
    .map(article => ({
      ...article,
      sections: article.children.filter(isContentType(ContentType.MarketingArticleSection)),
    }))
}
